import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { modelService } from "../services/model.service";

export const fetchModels = createAsyncThunk("model/fetchAll", async (_, thunkAPI) => {
  try {
    return await modelService.getModels();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch models");
  }
});

export const fetchActiveModels = createAsyncThunk("model/fetchActive", async (_, thunkAPI) => {
  try {
    return await modelService.getActiveModels();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch active models");
  }
});

export const fetchModelById = createAsyncThunk("model/fetchById", async (id, thunkAPI) => {
  try {
    return await modelService.getModelById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch model");
  }
});

export const addModel = createAsyncThunk("model/add", async (model, thunkAPI) => {
  try {
    return await modelService.createModel(model);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create model");
  }
});

export const editModel = createAsyncThunk("model/edit", async ({ id, model }, thunkAPI) => {
  try {
    return await modelService.updateModel(id, model);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to update model");
  }
});

export const removeModel = createAsyncThunk("model/remove", async (id, thunkAPI) => {
  try {
    await modelService.deleteModel(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to delete model");
  }
});

// --- IMPORT MODELS THUNK ---
export const importModels = createAsyncThunk(
  "model/import",
  async (file, { rejectWithValue }) => {
    try {
      const result = await modelService.importModels(file);
      return result;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to import models");
    }
  }
);

const initialState = {
  models: [],
  totalCount: 0,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

const modelSlice = createSlice({
  name: "model",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch models
      .addCase(fetchModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch active models
      .addCase(fetchActiveModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchActiveModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add model
      .addCase(addModel.pending, (state) => {
        state.loading = true;
      })
      .addCase(addModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models.push(action.payload);
      })
      .addCase(addModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Edit model
      .addCase(editModel.pending, (state) => {
        state.loading = true;
      })
      .addCase(editModel.fulfilled, (state, action) => {
        state.loading = false;
        const { id, model } = action.meta.arg;
        const index = state.models.findIndex((m) => m.id === id);
        if (index !== -1) {
          state.models[index] = { ...state.models[index], ...model, id };
        }
      })
      .addCase(editModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove model
      .addCase(removeModel.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models = state.models.filter((model) => model.id !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(removeModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // --- IMPORT MODELS REDUCERS ---
      .addCase(importModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importModels.fulfilled, (state) => {
        state.loading = false;
        // List will be refreshed by the component or uncomment dispatch(fetchModels()) in the thunk
      })
      .addCase(importModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openModal, closeModal, openDeleteModal, closeDeleteModal, clearError } = modelSlice.actions;

export default modelSlice.reducer;